import { useState } from "react";

import MuiButton from "@mui/material/Button";
import MuiIconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import useToggle from "core/hooks/useToggle";

import Icon from "ui/atom/Icon";
import Link from "ui/atom/Link";
import Modal from "ui/Modal";


Button.Clipboard = ({ value, i, onClick, icon = true, ...props }) => {
	const [copied, setCopied] = useState(false);

	const click = async () => {
		await navigator.clipboard.writeText(value).then(() => {
			setCopied(true);
			onClick && onClick();
			setTimeout(() => setCopied(false), 2000);
		});
	};

	if (icon) return <Button.Icon icon={copied ? "check" : i || "copy"} color="inherit" onClick={click} {...props} />;
	return <Button startIcon={<Icon name={copied ? "check" : i || "copy"} />} color="inherit" onClick={click} {...props}/>;
};

Button.Icon = ({ icon, size, fill, to, children, tab, ...props }) => (
	<MuiIconButton component={to ? Link : undefined} href={to ? to : undefined} target={tab ? "_blank" : undefined} rel={tab ? "noopener noreferrer" : undefined} {...props}>
		{icon && <Icon name={icon} size={size} fill={fill || "none"} />}
		{children}
	</MuiIconButton>
);

Button.Link = (props) => <Button component={Link} {...props}/>;

Button.Menu = ({ display, selected, options, select, ...props }) => {
	const [anchor, setAnchor] = useState(null);
	const open = Boolean(anchor);

	const handleOpen = (e) => setAnchor(e.currentTarget);
	const handleClose = () => setAnchor(null);

	return (
		<>
			<Button onClick={handleOpen} startIcon={<Icon name="chevron-down" />} {...props}>{display}</Button>
			<Menu
				open={open}
				anchorEl={anchor}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				onClick={handleClose}
				onClose={handleClose}
				keepMounted
			>
				{options.map((o, i) => <MenuItem selected={selected === o.value} onClick={() => select(o.value)} key={i}>{o.label}</MenuItem>)}
			</Menu>
		</>
	);
};

Button.Share = ({ url, title, text, icon, size, ...props }) => {
	const shareInfo = async () => await navigator.share({ title, text, url });

	return <Button.Icon icon={icon || "share"} size={size} onClick={shareInfo} {...props} />;
};

Button.Upload = ({ name, upload, children, ...props }) => {
	const style = {
		input: {
			display: "none"
		}
	};

	return (
		<Button
			component="label"
			variant="contained"
			tabIndex={-1}
			startIcon={<Icon name="cloud-upload" />}
			{...props}
		>
			{children}
			<input name={name} accept="image/*" type="file" onChange={(event) => upload(event.target.files[0])} style={style.input} />
		</Button>
	);
};

Button.Confirmation = (props) => {
	const [value, toggle] = useToggle();

	// common fields for the modal
	const { title, text, onClick } = props;

	const click = async () => {
		await onClick();
		toggle();
	};

	const actions = [
		{ label: "Cancel", variant: "outlined", color: "default", onClick: toggle },
		{ label: "Confirm", variant: "contained", color: "error", onClick: click }
	];

	return (
		<>
			{props.icon ? (
				(() => {
					const { icon, ...rest } = props;
					return <Button.Icon {...rest} icon={icon} onClick={toggle} />;
				})()
			) : (
				(() => {
					const { icon: _ignored, ...rest } = props;
					return <Button {...rest} onClick={toggle}/>;
				})()
			)}

			<Modal.Confirmation title={title} text={text} open={value} onClose={toggle} actions={actions}/>
		</>
	);
};

export default function Button(props) {
	return <MuiButton {...props} />;
}